import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, SlidersHorizontal, Sun, Moon, TrendUp, TrendDown, X, Clock, Target, ShieldCheck, Equals, Lock } from "phosphor-react";
import { format } from "date-fns";
import GeniLoader from "../components/GeniLoader";

export default function StopAdjustmentsDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get('type') || ''; // risk_reduction, breakeven, profit_lock
  const setupFilter = searchParams.get('setup') || '';

  const [loading, setLoading] = useState(true);
  const [trades, setTrades] = useState([]);
  const [setups, setSetups] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);

  // Sorting state
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tradesRes, setupsRes] = await Promise.all([
        api.get('/trades'),
        api.get('/setups')
      ]);
      setTrades(tradesRes.data);
      setSetups(setupsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Get trade type
  const getTradeType = (timeframe) => {
    if (!timeframe) return 'day';
    const tf = timeframe.toUpperCase();
    const swingTimeframes = ['4H', '6H', '8H', '12H', '1D', 'D', '1W', 'W'];
    return swingTimeframes.some(swTf => tf.includes(swTf)) ? 'swing' : 'day';
  };

  // Helper: Classify adjustment type based on final stop position
  const classifyAdjustmentType = (trade) => {
    const entry = trade.entry;
    const finalStop = trade.stop_loss;
    const direction = trade.direction;
    
    // Handle edge case: entry == stop (treat as break-even due to rounding)
    if (Math.abs(finalStop - entry) < 0.0001) {
      return 'breakeven';
    }
    
    if (direction === 'long') {
      // LONG: stop below entry = loss side (risk reduction), stop above entry = profit side
      if (finalStop < entry) return 'risk_reduction';
      if (finalStop > entry) return 'profit_lock';
      return 'breakeven';
    } else {
      // SHORT: stop above entry = loss side (risk reduction), stop below entry = profit side
      if (finalStop > entry) return 'risk_reduction';
      if (finalStop < entry) return 'profit_lock';
      return 'breakeven';
    }
  };

  // Get setup name by ID
  const getSetupName = (setupId) => {
    if (!setupId) return '—';
    const setup = setups.find(s => s.id === setupId);
    return setup?.name || '—';
  };

  // Process and filter trades
  const processedTrades = useMemo(() => {
    // Start with closed trades that have stop adjustments
    let eligibleTrades = trades.filter(t => 
      t.status === 'closed' && 
      (t.stop_adjusted === true || (t.stop_adjustment_history && t.stop_adjustment_history.length > 0))
    );

    // Apply setup filter if provided
    if (setupFilter) {
      if (setupFilter === '__no_setup__') {
        eligibleTrades = eligibleTrades.filter(t => !t.setup_id);
      } else {
        eligibleTrades = eligibleTrades.filter(t => t.setup_id === setupFilter);
      }
    }

    // Enrich with computed fields including adjustment type
    const enrichedTrades = eligibleTrades.map(t => ({
      ...t,
      _tradeType: getTradeType(t.timeframe),
      _adjustmentsCount: t.stop_adjustment_history?.length || 1,
      _initialStop: t.initial_stop_loss || t.stop_loss,
      _finalStop: t.stop_loss,
      _adjustmentType: classifyAdjustmentType(t)
    }));

    // Apply type filter if provided
    if (typeFilter) {
      return enrichedTrades.filter(t => t._adjustmentType === typeFilter);
    }

    return enrichedTrades;
  }, [trades, setupFilter]);

  // Sorted trades
  const sortedTrades = useMemo(() => {
    let sorted = [...processedTrades];

    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'date':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'adjustments':
          aVal = a._adjustmentsCount || 0;
          bVal = b._adjustmentsCount || 0;
          break;
        case 'asset':
          aVal = a.asset || '';
          bVal = b.asset || '';
          return sortDirection === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        default:
          return 0;
      }

      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });

    return sorted;
  }, [processedTrades, sortColumn, sortDirection]);

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Get trade type badge
  const getTradeTypeBadge = (tradeType) => {
    if (tradeType === 'swing') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
          <Moon size={10} weight="fill" />
          Swing
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
        <Sun size={10} weight="fill" />
        Day
      </span>
    );
  };

  // Get adjustment type badge
  const getAdjustmentTypeBadge = (adjustmentType) => {
    switch (adjustmentType) {
      case 'risk_reduction':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-[#FFA726]/10 text-[#FFA726] border border-[#FFA726]/20">
            <ShieldCheck size={10} weight="duotone" />
            Risk Reduction
          </span>
        );
      case 'breakeven':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-[#00B8D4]/10 text-[#00B8D4] border border-[#00B8D4]/20">
            <Equals size={10} weight="bold" />
            Break-even
          </span>
        );
      case 'profit_lock':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/20">
            <Lock size={10} weight="fill" />
            Profit Lock
          </span>
        );
      default:
        return null;
    }
  };

  // Get filter type label
  const getFilterTypeLabel = () => {
    switch (typeFilter) {
      case 'risk_reduction': return 'Risk Reduction Trades';
      case 'breakeven': return 'Break-even Trades';
      case 'profit_lock': return 'Profit Lock Trades';
      default: return 'All Adjusted Trades';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/advanced')}
            className="p-2 rounded-sm bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="back-to-advanced"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <SlidersHorizontal size={28} weight="duotone" className="text-cyan-400" />
            <div>
              <h1 className="text-2xl font-bold font-heading">{getFilterTypeLabel()}</h1>
              <p className="text-white/40 text-sm">
                Stop adjustment analysis
                {setupFilter && ` · ${setups.find(s => s.id === setupFilter)?.name || 'No setup'}`}
              </p>
            </div>
          </div>
        </div>

        {/* Active Filter Indicator */}
        {typeFilter && (
          <div className="mb-4 flex items-center gap-2 text-sm">
            <span className="text-white/40">Filtering by:</span>
            {getAdjustmentTypeBadge(typeFilter)}
            <button
              onClick={() => navigate(setupFilter ? `/advanced/stop-adjustments-trades?setup=${setupFilter}` : '/advanced/stop-adjustments-trades')}
              className="text-white/40 hover:text-white/60 ml-2 text-xs underline"
            >
              Clear filter
            </button>
          </div>
        )}

        {processedTrades.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <SlidersHorizontal size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/40 font-semibold">No {typeFilter ? getFilterTypeLabel().toLowerCase() : 'adjusted stop trades'} found</p>
            <p className="text-white/30 text-sm mt-1">
              {typeFilter ? 'Try removing the type filter or check other adjustment types.' : 
               setupFilter ? 'Try removing the setup filter or adjust stops on more trades.' : 
               'Adjust stops on open positions to track your stop management.'}
            </p>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Trades Table */}
            <div className={`${selectedTrade ? 'w-2/3' : 'w-full'} transition-all`}>
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                {/* Summary Stats */}
                <div className="p-4 border-b border-white/10 flex items-center gap-6 flex-wrap">
                  <div>
                    <div className="text-white/40 text-xs font-mono">SHOWING</div>
                    <div className="text-xl font-bold font-mono text-cyan-400">{sortedTrades.length}</div>
                  </div>
                  <div className="h-8 w-px bg-white/10" />
                  <div>
                    <div className="text-white/40 text-xs font-mono">RISK REDUCTION</div>
                    <div className="text-xl font-bold font-mono text-[#FFA726]">
                      {sortedTrades.filter(t => t._adjustmentType === 'risk_reduction').length}
                    </div>
                  </div>
                  <div className="h-8 w-px bg-white/10" />
                  <div>
                    <div className="text-white/40 text-xs font-mono">BREAK-EVEN</div>
                    <div className="text-xl font-bold font-mono text-[#00B8D4]">
                      {sortedTrades.filter(t => t._adjustmentType === 'breakeven').length}
                    </div>
                  </div>
                  <div className="h-8 w-px bg-white/10" />
                  <div>
                    <div className="text-white/40 text-xs font-mono">PROFIT LOCK</div>
                    <div className="text-xl font-bold font-mono text-[#00E676]">
                      {sortedTrades.filter(t => t._adjustmentType === 'profit_lock').length}
                    </div>
                  </div>
                </div>

                <table className="w-full" data-testid="stop-adjustments-trades-table">
                  <thead className="bg-[#0A0A0A]">
                    <tr className="text-left text-xs text-white/40 font-mono">
                      <th 
                        className="p-3 cursor-pointer hover:text-white transition-colors"
                        onClick={() => handleSort('asset')}
                      >
                        ASSET {sortColumn === 'asset' && (sortDirection === 'desc' ? '↓' : '↑')}
                      </th>
                      <th className="p-3">DIRECTION</th>
                      <th className="p-3">TYPE</th>
                      <th className="p-3">ADJUSTMENT TYPE</th>
                      <th className="p-3">SETUP</th>
                      <th className="p-3">INITIAL STOP</th>
                      <th className="p-3">FINAL STOP</th>
                      <th 
                        className="p-3 cursor-pointer hover:text-white transition-colors"
                        onClick={() => handleSort('adjustments')}
                      >
                        # ADJ {sortColumn === 'adjustments' && (sortDirection === 'desc' ? '↓' : '↑')}
                      </th>
                      <th 
                        className="p-3 cursor-pointer hover:text-white transition-colors"
                        onClick={() => handleSort('date')}
                      >
                        DATE {sortColumn === 'date' && (sortDirection === 'desc' ? '↓' : '↑')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTrades.map((trade) => (
                      <tr
                        key={trade.id}
                        className={`border-t border-white/10 cursor-pointer hover:bg-white/5 transition-colors ${
                          selectedTrade?.id === trade.id ? 'bg-cyan-500/10' : ''
                        }`}
                        onClick={() => setSelectedTrade(trade)}
                        data-testid={`trade-row-${trade.id}`}
                      >
                        <td className="p-3">
                          <div className="font-mono text-white font-bold">{trade.asset}</div>
                          <div className="text-xs text-white/40">{trade.timeframe || '-'}</div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {trade.direction === "long" ? (
                              <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                            ) : (
                              <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                            )}
                            <span
                              className={`font-mono text-sm uppercase ${
                                trade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                              }`}
                            >
                              {trade.direction}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          {getTradeTypeBadge(trade._tradeType)}
                        </td>
                        <td className="p-3">
                          {getAdjustmentTypeBadge(trade._adjustmentType)}
                        </td>
                        <td className="p-3">
                          <span className="text-white/60 text-sm">{getSetupName(trade.setup_id)}</span>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-white/60">
                            ${trade._initialStop?.toLocaleString()}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-cyan-400">
                            ${trade._finalStop?.toLocaleString()}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                            {trade._adjustmentsCount}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-white/60 text-sm">
                            {trade.created_at && format(new Date(trade.created_at), "MMM d, yyyy")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Results count */}
                <div className="p-3 border-t border-white/10 text-xs text-white/40">
                  Showing {sortedTrades.length} adjusted stop trades
                </div>
              </div>
            </div>

            {/* Trade Detail Panel */}
            {selectedTrade && (
              <div className="w-1/3 bg-[#121212] border border-white/10 rounded-sm p-5 h-fit sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold font-heading">{selectedTrade.asset}</h3>
                  <button
                    onClick={() => setSelectedTrade(null)}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                  >
                    <X size={18} className="text-white/40" />
                  </button>
                </div>

                {/* Trade Overview */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-[#0A0A0A] rounded p-3">
                    <div className="text-white/40 text-[10px] font-mono">DIRECTION</div>
                    <div className={`font-mono font-bold ${
                      selectedTrade.direction === 'long' ? 'text-[#00E676]' : 'text-[#FF3B30]'
                    }`}>
                      {selectedTrade.direction?.toUpperCase()}
                    </div>
                  </div>
                  <div className="bg-[#0A0A0A] rounded p-3">
                    <div className="text-white/40 text-[10px] font-mono">TYPE</div>
                    <div className="font-mono text-white">
                      {selectedTrade._tradeType === 'swing' ? 'Swing' : 'Day'}
                    </div>
                  </div>
                </div>

                {/* Entry */}
                <div className="bg-[#0A0A0A] rounded p-3 mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Target size={14} className="text-white/40" />
                    <span className="text-white/40 text-[10px] font-mono">ENTRY PRICE</span>
                  </div>
                  <div className="font-mono text-lg text-white">${selectedTrade.entry?.toLocaleString()}</div>
                </div>

                {/* Stop Adjustment Details */}
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <SlidersHorizontal size={16} className="text-cyan-400" />
                    <span className="text-cyan-400 text-xs font-mono uppercase">Stop Adjustment Summary</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-white/60 text-sm">Adjustment Type</span>
                      {getAdjustmentTypeBadge(selectedTrade._adjustmentType)}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60 text-sm">Initial Stop</span>
                      <span className="font-mono text-white/70">${selectedTrade._initialStop?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60 text-sm">Final Stop</span>
                      <span className={`font-mono font-bold ${
                        selectedTrade._adjustmentType === 'profit_lock' ? 'text-[#00E676]' :
                        selectedTrade._adjustmentType === 'breakeven' ? 'text-[#00B8D4]' :
                        'text-[#FFA726]'
                      }`}>
                        ${selectedTrade._finalStop?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60 text-sm">Stop Adjusted</span>
                      <span className="text-cyan-400 font-bold">Yes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60 text-sm">Number of Adjustments</span>
                      <span className="font-mono text-cyan-400 font-bold">{selectedTrade._adjustmentsCount}</span>
                    </div>
                  </div>
                </div>

                {/* Adjustment History (if available) */}
                {selectedTrade.stop_adjustment_history && selectedTrade.stop_adjustment_history.length > 0 && (
                  <div className="bg-[#0A0A0A] border border-white/10 rounded p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock size={14} className="text-white/40" />
                      <span className="text-white/40 text-xs font-mono uppercase">Adjustment Timeline</span>
                    </div>
                    <div className="space-y-3 max-h-48 overflow-y-auto">
                      {selectedTrade.stop_adjustment_history.map((adj, idx) => (
                        <div key={idx} className="border-l-2 border-cyan-500/30 pl-3 py-1">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-white/50">${adj.old_stop?.toLocaleString()}</span>
                            <span className="text-white/30">→</span>
                            <span className="text-cyan-400 font-mono font-bold">${adj.new_stop?.toLocaleString()}</span>
                          </div>
                          {adj.timestamp && (
                            <div className="text-white/30 text-[10px]">
                              {format(new Date(adj.timestamp), "MMM d, yyyy HH:mm")}
                            </div>
                          )}
                          {adj.is_profit_locking && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E676]/10 text-[#00E676]">
                              Profit Lock
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Outcome */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Outcome</span>
                    <span className={`font-bold ${
                      selectedTrade.outcome === 'win' ? 'text-[#00E676]' :
                      selectedTrade.outcome === 'loss' ? 'text-[#FF3B30]' :
                      'text-white/60'
                    }`}>
                      {selectedTrade.outcome?.toUpperCase()}
                    </span>
                  </div>
                  {selectedTrade.profit_usd !== null && (
                    <div className="flex justify-between text-sm mt-2">
                      <span className="text-white/60">Profit</span>
                      <span className={`font-mono font-bold ${
                        selectedTrade.profit_usd > 0 ? 'text-[#00E676]' :
                        selectedTrade.profit_usd < 0 ? 'text-[#FF3B30]' :
                        'text-white/60'
                      }`}>
                        {selectedTrade.profit_usd >= 0 ? '+' : ''}${selectedTrade.profit_usd?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
